// src/pages/OverviewPage.jsx
import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/AuthContext';
import { fmtMoney, fmtPct, fmtNum, colorClass, LoadingOverlay } from '../components';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis } from 'recharts';

const COLORS = ['#9b1c1c','#5a5a6e','#6e6a5a','#4a5a6e','#6e5a6a','#5a6e5a','#7a7a8e','#8a7a6e','#6e8a6a','#4a6e8a'];

const PERIOD_OPTIONS = [
  { label: '1D',      days: 1 },
  { label: '1W',      days: 7 },
  { label: '1M',      days: 30 },
  { label: '3M',      days: 90 },
  { label: 'YTD',     days: 'ytd' },
  { label: '1Y',      days: 365 },
  { label: 'All time', days: null },
];

function getPeriodStart(period) {
  if (!period) return null;
  if (period === 'ytd') {
    const now = new Date();
    return new Date(now.getFullYear(), 0, 1).toISOString();
  }
  return new Date(Date.now() - period * 86400 * 1000).toISOString();
}

export default function OverviewPage() {
  const { canEdit } = useAuth();
  const [sleeves, setSleeves] = useState([]);
  const [positions, setPositions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [alertsExpanded, setAlertsExpanded] = useState(false);
  const [computedAlerts, setComputedAlerts] = useState([]);
  const [computedAlertMeta, setComputedAlertMeta] = useState(null);

  const [realizedByPeriod, setRealizedByPeriod] = useState({});
  const [loading, setLoading] = useState(true);
  const [pnlPeriod, setPnlPeriod] = useState(null); // null = all time
  const [pulling, setPulling] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    setLoading(true);
    const [{ data: sleeveData }, { data: posData }, { data: alertData }] = await Promise.all([
      supabase.from('sleeves').select('*').eq('is_active', true).order('name'),
      supabase.from('positions').select('*, securities(*)').eq('is_active', true),
      supabase.from('alert_events').select('*').eq('is_acknowledged', false)
        .order('triggered_at', { ascending: false }).limit(10),
    ]);
    setSleeves(sleeveData || []);
    setPositions(posData || []);
    setAlerts(alertData || []);
    buildComputedAlerts(posData || []);

    // Pre-fetch realized P&L totals for each period
    const periods = {};
    await Promise.all(PERIOD_OPTIONS.map(async ({ days }) => {
      const since = getPeriodStart(days);
      let q = supabase.from('position_trades').select('realized_pnl').eq('trade_type', 'exit');
      if (since) q = q.gte('traded_at', since);
      const { data } = await q;
      periods[days ?? 'all'] = (data || []).reduce((s, t) => s + (t.realized_pnl || 0), 0);
    }));
    setRealizedByPeriod(periods);
    setLoading(false);
  }

  async function handlePullData() {
    if (!canEdit) return;
    setPulling(true);
    setSuccessMsg('');
    try {
      const { data: pos } = await supabase.from('positions').select('*, securities(*)').eq('is_active', true);
      const tickers = [...new Set((pos || []).map(p => p.securities?.ticker).filter(Boolean))];
      for (const p of (pos || [])) {
        const tkr = p.securities?.ticker;
        if (!tkr) continue;
        const { data, error } = await supabase.functions.invoke('security-lookup', {
          body: { action: 'live_price', identifier: tkr }
        });
        if (error) continue;
        const px = Number(data?.price);
        if (!Number.isFinite(px)) continue;
        const qty = Number(p.quantity || 0);
        const mv = p.direction === 'long' ? qty * px : -qty * px;
        const ep = Number(p.entry_price || 0);
        const upl = p.direction === 'long' ? (px - ep) * qty : (ep - px) * qty;
        const upct = ep > 0 ? (upl / Math.abs(qty * ep)) * 100 : null;
        await supabase.from('positions').update({
          current_price: px,
          market_value: mv,
          unrealized_pnl: upl,
          unrealized_pnl_pct: upct,
        }).eq('id', p.id);
        if (p.securities?.id) {
          await supabase.from('securities').update({
            last_price: px,
            price_updated_at: new Date().toISOString(),
          }).eq('id', p.securities.id);
        }
      }
      setSuccessMsg(`Pulled live prices for ${tickers.length} securities`);
      setTimeout(() => setSuccessMsg(''), 3000);
    } finally {
      setPulling(false);
      loadData();
    }
  }


  async function buildComputedAlerts(pos) {
    // Trigger thresholds (explicit)
    const THRESH = {
      max_position_gross_pct: 5,      // > 5% of gross exposure
      day_loss_pct: -2.5,             // < -2.5% over ~1 trading day
      week_gain_pct: 10,              // > +10% over ~5 trading days
      mo_reversal: true               // monthly return sign flip vs prior month
    };

    const gross = pos.reduce((s,p)=>s+Math.abs(p.market_value||0),0) || 0;
    const alertsOut = [];

    // Position sizing alerts (>5% gross)
    for (const p of pos) {
      const pct = gross>0 ? (Math.abs(p.market_value||0)/gross*100) : 0;
      if (pct > THRESH.max_position_gross_pct) {
        alertsOut.push({
          severity: pct >= (THRESH.max_position_gross_pct*1.5) ? 'critical' : 'warning',
          type: 'position_size',
          title: `${p.securities?.ticker || p.cusip} exceeds ${THRESH.max_position_gross_pct}% gross`,
          detail: `Weight: ${pct.toFixed(2)}% of gross`,
          key: `pos_${p.id}`
        });
      }
    }

    // Price-based alerts (1D, 1W, monthly reversal)
    // Uses cached price_history when available; otherwise triggers only on Pull Data in Risk tab.
    try {
      const ids = [...new Set(pos.map(p=>p.security_id).filter(Boolean))];
      if (ids.length) {
        const { data: ph } = await supabase
          .from('price_history')
          .select('security_id, date, close')
          .in('security_id', ids)
          .order('date', { ascending: true });

        const byId = {};
        for (const r of (ph||[])) {
          (byId[r.security_id] ||= []).push({ date: r.date, close: Number(r.close) });
        }

        for (const p of pos) {
          const series = byId[p.security_id] || [];
          if (series.length < 8) continue;

          const last = series[series.length-1].close;
          const prev1 = series[series.length-2].close;
          const prev5 = series.length>=6 ? series[series.length-6].close : null;

          if (prev1 && prev1>0) {
            const r1 = (last/prev1-1)*100;
            if (r1 < THRESH.day_loss_pct) {
              alertsOut.push({
                severity: r1 < THRESH.day_loss_pct*2 ? 'critical' : 'warning',
                type: 'day_move',
                title: `${p.securities?.ticker || p.cusip} down > ${Math.abs(THRESH.day_loss_pct)}% (1D)`,
                detail: `1D: ${r1.toFixed(2)}%`,
                key: `d_${p.id}`
              });
            }
          }

          if (prev5 && prev5>0) {
            const r5 = (last/prev5-1)*100;
            if (r5 > THRESH.week_gain_pct) {
              alertsOut.push({
                severity: r5 > THRESH.week_gain_pct*2 ? 'critical' : 'warning',
                type: 'week_move',
                title: `${p.securities?.ticker || p.cusip} up > ${THRESH.week_gain_pct}% (1W)`,
                detail: `1W: ${r5.toFixed(2)}%`,
                key: `w_${p.id}`
              });
            }
          }

          // Monthly reversal: sign flip between last 21 trading days and prior 21
          if (series.length >= 45) {
            const a0 = series[series.length-1].close;
            const a21 = series[series.length-22].close;
            const a42 = series[series.length-43].close;
            if (a21 && a42 && a21>0 && a42>0) {
              const m1 = (a0/a21-1)*100;
              const m0 = (a21/a42-1)*100;
              if ((m1>0 && m0<0) || (m1<0 && m0>0)) {
                alertsOut.push({
                  severity: 'warning',
                  type: 'mo_reversal',
                  title: `${p.securities?.ticker || p.cusip} monthly reversal`,
                  detail: `Prev Mo: ${m0.toFixed(2)}% → Curr Mo: ${m1.toFixed(2)}%`,
                  key: `mo_${p.id}`
                });
              }
            }
          }
        }
      }
    } catch {
      // no-op; price_history may be empty until Pull Data is run
    }

    // Other relevant alerts: missing sector / asset class metadata
    const missingSector = pos.filter(p=>!p.securities?.sector).length;
    if (missingSector) {
      alertsOut.push({
        severity: 'info',
        type: 'data_quality',
        title: `Missing sector for ${missingSector} security${missingSector!==1?'ies':''}`,
        detail: 'Sector weights may be incomplete until populated.',
        key: 'missing_sector'
      });
    }
    const missingAC = pos.filter(p=>!p.securities?.asset_class).length;
    if (missingAC) {
      alertsOut.push({
        severity: 'info',
        type: 'data_quality',
        title: `Missing asset class for ${missingAC} security${missingAC!==1?'ies':''}`,
        detail: 'Asset class weights may be incomplete until populated.',
        key: 'missing_ac'
      });
    }

    setComputedAlertMeta(THRESH);
    setComputedAlerts(alertsOut);
  }

  if (loading) return <LoadingOverlay message="Loading portfolio..." />;

  const totalLong  = positions.filter(p => p.direction === 'long').reduce((s, p) => s + (p.market_value || 0), 0);
  const totalShort = Math.abs(positions.filter(p => p.direction === 'short').reduce((s, p) => s + (p.market_value || 0), 0));
  const totalGross = totalLong + totalShort;
  const totalNet   = totalLong - totalShort;

  // Unrealized P&L — always current (no time filter possible on open positions)
  const unrealizedPnl     = positions.reduce((s, p) => s + (p.unrealized_pnl || 0), 0);
  const totalEntryValue   = positions.reduce((s, p) => s + Math.abs((p.quantity || 0) * (p.entry_price || 0)), 0);
  const unrealizedPnlPct  = totalEntryValue > 0 ? (unrealizedPnl / totalEntryValue) * 100 : 0;

  // Realized P&L for selected period
  const periodKey     = pnlPeriod ?? 'all';
  const realizedPnl   = realizedByPeriod[periodKey] ?? 0;
  // Combined P&L = unrealized (current) + realized (in period)
  const combinedPnl   = unrealizedPnl + realizedPnl;
  const combinedPnlPct = totalEntryValue > 0 ? (combinedPnl / totalEntryValue) * 100 : 0;

  // Sector breakdown using securities.sector (GICS)
  const sectorMap = {};
  for (const p of positions) {
    const sec = p.securities?.sector || 'Unknown';
    sectorMap[sec] = (sectorMap[sec] || 0) + Math.abs(p.market_value || 0);
  }
  const sectorData = Object.entries(sectorMap)
    .map(([name, value]) => ({ name, value, pct: totalGross > 0 ? value / totalGross * 100 : 0 }))
    .sort((a, b) => b.value - a.value);

  const sleeveMap = {};
  for (const p of positions) sleeveMap[p.sleeve_id] = (sleeveMap[p.sleeve_id] || 0) + Math.abs(p.market_value || 0);
  const sleeveData = sleeves.map(s => ({ name: s.name, value: sleeveMap[s.id] || 0 })).filter(s => s.value > 0);

  const acMap = {};
  for (const p of positions) {
    const ac = p.securities?.asset_class || 'other';
    acMap[ac] = (acMap[ac] || 0) + Math.abs(p.market_value || 0);
  }
  const acData = Object.entries(acMap).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  const priceUpdated = positions.reduce((d, p) => {
    const t = p.securities?.price_updated_at;
    return !d || (t && t > d) ? t : d;
  }, null);

  const selectedPeriodLabel = PERIOD_OPTIONS.find(o => o.days === pnlPeriod)?.label || 'All time';

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Fund Overview</div>
          <div className="page-subtitle">
            {positions.length} active positions across {sleeves.length} sleeve{sleeves.length !== 1 ? 's' : ''}
            {priceUpdated && (
              <span style={{ marginLeft: 12, fontSize: 11, fontFamily: 'var(--font-mono)', color: 'var(--text-lo)' }}>
                Prices as of {new Date(priceUpdated).toLocaleString()}
              </span>
            )}
          </div>
        </div>
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <button className="btn btn-secondary btn-sm" onClick={loadData} disabled={pulling}>↻ Refresh</button>
          {canEdit && (
            <button className="btn btn-secondary btn-sm" onClick={handlePullData} disabled={pulling}>
              {pulling ? '↺ Pulling...' : '↺ Pull Data'}
            </button>
          )}
        </div>
      </div>

      <div className="page-body section-gap">
        {successMsg && <div className="alert alert-success">✓ {successMsg}</div>}
        {alerts.length > 0 && (
          <div>
            {alerts.slice(0, 3).map(a => (
              <div key={a.id} className={`alert alert-${a.severity === 'critical' ? 'critical' : 'warning'}`}>
                <span>⚠</span><span>{a.message}</span>
              </div>
            ))}
          </div>
        )}

        {/* Exposure stats */}
        <div className="stat-grid stat-grid-4">
          <div className="stat-card">
            <div className="stat-label">Gross Exposure</div>
            <div className="stat-value">{fmtMoney(totalGross)}</div>
            <div className="stat-sub">{totalGross > 0 ? `Long ${fmtPct(totalLong / totalGross * 100, 0)} / Short ${fmtPct(totalShort / totalGross * 100, 0)}` : '—'}</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Net Exposure</div>
            <div className={`stat-value ${colorClass(totalNet)}`}>{fmtMoney(totalNet)}</div>
            <div className="stat-sub">{fmtPct(totalGross > 0 ? totalNet / totalGross * 100 : 0, 0)} of Gross</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Long Exposure</div>
            <div className="stat-value positive">{fmtMoney(totalLong)}</div>
            <div className="stat-sub">{positions.filter(p => p.direction === 'long').length} positions</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Short Exposure</div>
            <div className="stat-value negative">{fmtMoney(totalShort)}</div>
            <div className="stat-sub">{positions.filter(p => p.direction === 'short').length} positions</div>
          </div>
        </div>

        {/* P&L — combined realized + unrealized with period selector */}
        <div className="card">
          <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
            <span className="card-title">P&L</span>
            <div style={{ display: 'flex', gap: 4, marginLeft: 'auto', flexWrap: 'wrap' }}>
              {PERIOD_OPTIONS.map(({ label, days }) => (
                <button
                  key={label}
                  className={`btn btn-sm ${pnlPeriod === days ? 'btn-primary' : 'btn-secondary'}`}
                  style={{ padding: '2px 10px', fontSize: 11 }}
                  onClick={() => setPnlPeriod(days)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="card-body">
            <div className="stat-grid stat-grid-4">
              <div className="stat-card" style={{ background: 'var(--bg-base)' }}>
                <div className="stat-label">Total P&L ({selectedPeriodLabel})</div>
                <div className={`stat-value ${combinedPnlPct >= 0 ? 'positive' : 'negative'}`} style={{ fontSize: 22 }}>{fmtPct(combinedPnlPct)}</div>
                <div className={`stat-change ${colorClass(combinedPnl)}`}>{fmtMoney(combinedPnl)}</div>
                <div className="stat-sub" style={{ marginTop: 4 }}>realized + unrealized</div>
              </div>
              <div className="stat-card" style={{ background: 'var(--bg-base)' }}>
                <div className="stat-label">Unrealized P&L (current)</div>
                <div className={`stat-value ${unrealizedPnlPct >= 0 ? 'positive' : 'negative'}`}>{fmtPct(unrealizedPnlPct)}</div>
                <div className={`stat-change ${colorClass(unrealizedPnl)}`}>{fmtMoney(unrealizedPnl)}</div>
                <div className="stat-sub">{positions.length} open position{positions.length !== 1 ? 's' : ''}</div>
              </div>
              <div className="stat-card" style={{ background: 'var(--bg-base)' }}>
                <div className="stat-label">Realized P&L ({selectedPeriodLabel})</div>
                <div className={`stat-value ${colorClass(realizedPnl)}`}>{fmtMoney(realizedPnl)}</div>
                <div className="stat-sub">closed positions</div>
              </div>
              <div
                className="stat-card"
                style={{ background: 'var(--bg-base)', cursor: 'pointer' }}
                onClick={() => setAlertsExpanded(v => !v)}
                title="Click to expand"
              >
                <div className="stat-label">Active Alerts</div>
                <div className={`stat-value ${(alerts.length + computedAlerts.length) > 0 ? 'negative' : 'positive'}`}>{alerts.length + computedAlerts.length}</div>
                <div className="stat-sub">
                  triggers: {computedAlertMeta?.max_position_gross_pct ?? 5}% gross | {Math.abs(computedAlertMeta?.day_loss_pct ?? -2.5)}% 1D loss | {(computedAlertMeta?.week_gain_pct ?? 10)}% 1W gain | mo reversal
                </div>
              </div>
            </div>
            {alertsExpanded && (
              <div style={{ marginTop: 10 }}>
                <div className="card-subtitle">Active Alerts ({alerts.length + computedAlerts.length})</div>
                <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                  Trigger thresholds: Position &gt; {computedAlertMeta?.max_position_gross_pct ?? 5}% of gross; 1D move &lt; {computedAlertMeta?.day_loss_pct ?? -2.5}%; 1W move &gt; {computedAlertMeta?.week_gain_pct ?? 10}%; monthly reversal = sign flip in trailing monthly return.
                </div>
                <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                  {[...computedAlerts, ...alerts.map(a => ({
                    severity: a.severity || 'warning',
                    type: 'table',
                    title: a.title || a.rule_name || 'Alert',
                    detail: a.message || (a.details ? JSON.stringify(a.details) : ''),
                    key: a.id
                  }))].map(a => (
                    <div key={a.key} className={`alert alert-${a.severity === 'critical' ? 'danger' : (a.severity === 'warning' ? 'warning' : 'info')}`} style={{ margin: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <div><b>{a.title}</b>{a.detail ? <span className="muted"> — {a.detail}</span> : null}</div>
                        <div className="muted" style={{ textTransform: 'uppercase', fontSize: 11 }}>{a.type}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>

        {/* Charts row */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
          {sectorData.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Sector Weights</span></div>
              <div className="card-body">
                {sectorData.some(s => s.name !== 'Unknown') ? (
                  <>
                    <ResponsiveContainer width="100%" height={160}>
                      <PieChart>
                        <Pie data={sectorData.filter(s => s.name !== 'Unknown')} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65}>
                          {sectorData.filter(s => s.name !== 'Unknown').map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => fmtMoney(v)} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ marginTop: 6 }}>
                      {sectorData.slice(0, 5).map((s, i) => (
                        <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <div style={{ width: 7, height: 7, borderRadius: 2, background: COLORS[i % COLORS.length] }} />
                            <span style={{ fontSize: 10, color: 'var(--text-mid)', fontFamily: 'var(--font-mono)' }}>{s.name}</span>
                          </div>
                          <span style={{ fontSize: 10, color: 'var(--text-lo)', fontFamily: 'var(--font-mono)' }}>{s.pct.toFixed(1)}%</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--text-lo)', lineHeight: 1.6 }}>
                    No sector data yet. Re-add positions via CUSIP lookup to populate GICS sectors.
                  </div>
                )}
              </div>
            </div>
          )}

          {sleeveData.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Sleeve Allocation</span></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={160}>
                  <PieChart>
                    <Pie data={sleeveData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65}>
                      {sleeveData.map((_, i) => <Cell key={i} fill={COLORS[(i + 3) % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={v => fmtMoney(v)} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
                <div style={{ marginTop: 6 }}>
                  {sleeveData.map((s, i) => (
                    <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{ width: 7, height: 7, borderRadius: 2, background: COLORS[(i + 3) % COLORS.length] }} />
                        <span style={{ fontSize: 10, color: 'var(--text-mid)', fontFamily: 'var(--font-mono)' }}>{s.name}</span>
                      </div>
                      <span style={{ fontSize: 10, color: 'var(--text-lo)', fontFamily: 'var(--font-mono)' }}>{fmtMoney(s.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {acData.length > 0 && (
            <div className="card">
              <div className="card-header"><span className="card-title">Asset Class</span></div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={160}>
                  <BarChart data={acData} layout="vertical">
                    <XAxis type="number" hide />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10, fontFamily: 'var(--font-mono)', fill: 'var(--text-lo)' }} width={80} />
                    <Bar dataKey="value" radius={[0, 3, 3, 0]}>
                      {acData.map((_, i) => <Cell key={i} fill={COLORS[(i + 5) % COLORS.length]} />)}
                    </Bar>
                    <Tooltip formatter={v => fmtMoney(v)} contentStyle={{ background: 'var(--bg-card)', border: '1px solid var(--border)', fontSize: 12 }} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        {/* Sleeve summary table */}
        {sleeves.length > 0 && (
          <div className="card">
            <div className="card-header"><span className="card-title">Sleeve Summary</span></div>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Sleeve</th>
                    <th>Positions</th>
                    <th>Long</th>
                    <th>Short</th>
                    <th>Net</th>
                    <th>Gross</th>
                    <th>Unr. P&L</th>
                    <th>Real. P&L ({selectedPeriodLabel})</th>
                    <th>Total P&L</th>
                    <th>Benchmark</th>
                  </tr>
                </thead>
                <tbody>
                  {sleeves.map(s => {
                    const sp = positions.filter(p => p.sleeve_id === s.id);
                    const lng = sp.filter(p => p.direction === 'long').reduce((a, p) => a + (p.market_value || 0), 0);
                    const sht = Math.abs(sp.filter(p => p.direction === 'short').reduce((a, p) => a + (p.market_value || 0), 0));
                    const unr = sp.reduce((a, p) => a + (p.unrealized_pnl || 0), 0);
                    // We don't have per-sleeve realized in the pre-fetched map, show fund-level total only for "all sleeves" row
                    return (
                      <tr key={s.id}>
                        <td className="primary" style={{ fontWeight: 500 }}>{s.name}</td>
                        <td>{sp.length}</td>
                        <td className="positive">{fmtMoney(lng)}</td>
                        <td className="negative">{fmtMoney(sht)}</td>
                        <td className={colorClass(lng - sht)}>{fmtMoney(lng - sht)}</td>
                        <td>{fmtMoney(lng + sht)}</td>
                        <td className={colorClass(unr)}>{fmtMoney(unr)}</td>
                        <td style={{ color: 'var(--text-lo)', fontSize: 11 }}>—</td>
                        <td className={colorClass(unr)}>{fmtMoney(unr)}</td>
                        <td className="accent">{s.benchmark || 'SPY'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {!sleeves.length && (
          <div className="card">
            <div className="empty-state">
              <div className="empty-state-icon">◈</div>
              <div className="empty-state-title">No sleeves yet</div>
              <div className="empty-state-desc">Go to Admin to create your first sleeve, then add positions.</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
